<template>
    <div>
        <v-app-bar flat color="transparent">
            <v-row dense align="center">
                <v-col cols="auto">
                    <slot name="breadcrumbs"></slot>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto">
                    <v-btn
                        color="success white--text"
                        class="text-capitalize"
                        @click="
                            create = true, 
                            update = false, 
                            deliveryStatusDialog = true
                        "
                    >
                        <v-icon size="25" class="me-1">mdi-plus</v-icon>
                        {{$t('DeliveryStatus.create-status')}}
                    </v-btn>
                </v-col>
            </v-row>
        </v-app-bar>
        <v-container fluid>
            <bee-handy-table
                :headers="headers"
                :items="deliveryStatus"
                dense
                fixed-header
                :loading="loading"
                height="70vh"
                pagination-on-scroll
                shown-rows-text=""
            >
                <template v-slot:item.isFinalStatus="{ item }">
                    <div v-if="item.is_final_status === true" class="mr-10">
                        <v-icon>mdi-check-circle-outline</v-icon>
                    </div>
                </template>
                <template v-slot:item.actions="{ item }">
                    <div>
                        <v-spacer></v-spacer>
                        <v-btn 
                            small
                            depressed
                            color="transparent"
                            class="warning--text"
                            @click="
                                update = true, 
                                create = false, 
                                deliveryStatusId = item.id, 
                                setInputsValues(deliveryStatusId),  
                                deliveryStatusDialog = true
                            "
                        >
                            <v-icon>
                                mdi-pencil
                            </v-icon>
                        </v-btn>
                        <v-btn 
                            small
                            depressed
                            color="transparent"
                            class="red--text"
                            @click="
                                deliveryStatusId = item.id, 
                                deleteDialog = true
                            "
                        >
                            <v-icon>
                                mdi-delete
                            </v-icon>
                        </v-btn>
                    </div>
                </template>
            </bee-handy-table>
        </v-container>

        <!-- Add / edit delivery status dialog -->
        <v-dialog
            v-model="deliveryStatusDialog"
            max-width="500"
            persistent
        >
            <v-form ref="form" :disabled="submitLoading" @submit.prevent="submit((update === true && create === false) ? deliveryStatusId : '')">
                <v-card class="rounded-lg">
                    <v-card-title>
                        <div v-if="create === true && update === false">
                            <h4>{{$t('DeliveryStatus.create-delivery-status')}}</h4>
                        </div>
                        <div v-if="create === false && update === true">
                            <h4>{{$t('DeliveryStatus.edit-delivery-status')}}</h4>
                        </div>
                    </v-card-title>
                    <v-card-text>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="name"
                                    outlined
                                    dense
                                    hide-details
                                    :label="$t('DeliveryStatus.inputs.delivery-name')"
                                    class="rounded-lg"
                                    :rules="[rules.required]"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="invoiceType"
                                    :items="invoicesTypes"
                                    item-text="name"
                                    item-value="id"
                                    outlined
                                    dense
                                    hide-details
                                    :label="$t('DeliveryStatus.inputs.invoice-type')"
                                    class="rounded-lg"
                                    :rules="[rules.required]"
                                ></v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row dense class="mt-3">
                            <div class="d-flex">
                                <v-checkbox
                                    v-model="isFinalStatus"
                                    dense
                                    hide-details
                                    class="pa-0 mt-1"
                                ></v-checkbox>
                                <span class="d-flex align-center mt-0">
                                    {{ $t('DeliveryStatus.inputs.final-status') }}
                                </span>
                            </div>
                        </v-row>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn
                            v-if="create === true && update === false"
                            class="green white--text text-capitalize"
                            :loading="submitLoading"
                            type="submit">
                            {{ $t('DeliveryStatus.buttons.add') }}
                        </v-btn>
                        <v-btn
                            v-if="create === false && update === true"
                            class="warning white--text text-capitalize"
                            :loading="submitLoading"
                            type="submit">
                            {{ $t('DeliveryStatus.buttons.edit') }}
                        </v-btn>
                        <v-btn
                            class="grey white--text text-capitalize"
                            @click="deliveryStatusDialog = false, $refs.form.reset()">
                            {{ $t('DeliveryStatus.buttons.cancel') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-form>
        </v-dialog>

        <!-- delete dialog -->
        <v-dialog
            v-model="deleteDialog"
            max-width="500"
            persistent
        >
            <v-card class="rounded-lg">
                <v-card-title>
                    {{ $t('DeliveryStatus.delete.delete-delivery-status') }}
                </v-card-title>
    
                <v-card-text>
                    {{ $t('DeliveryStatus.delete.delete-dialog-content') }}
                </v-card-text>
    
                <v-card-actions>
                    <v-btn
                        class="red white--text text-capitalize"
                        :loading="submitLoading"
                        @click="deleteDelivery(deliveryStatusId)"
                        >
                        {{ $t('DeliveryStatus.buttons.delete') }}
                    </v-btn>
                    <v-btn
                        class="grey white--text text-capitalize"
                        @click="deleteDialog = false">
                        {{ $t('DeliveryStatus.buttons.cancel') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import { mapState } from 'vuex';
import { invoicesTypes } from '@/helpers/enums';
import rules from '@/validation';

export default {
    data() {
        return {
            // dialogs
            deliveryStatusDialog: false,
            deleteDialog: false,

            // loadings
            loading: false,
            submitLoading: false,

            // conditions
            create: false,
            update: false,

            // models
            name: null,
            invoiceType: null,
            isFinalStatus: false,

            // enums
            invoicesTypes,

            // rules
            rules: rules.rules
        }
    },
    methods: {
        submit(deliveryStatusId) {
            // console.log(deliveryStatusId)
            if (this.$refs.form.validate()) {
                this.submitLoading = true
                this.loading = true
                const actionName = this.create === true ? 'create' : 'update'
                this.$store.dispatch(`deliveryStatus/${actionName}`, {
                    name: this.name,
                    invoiceType: this.invoiceType,
                    isFinalStatus: this.isFinalStatus,
                    deliveryStatusId: deliveryStatusId
                }).then(() => {
                    this.updateTable()
                })
                .finally(() => {
                    this.deliveryStatusDialog = false
                    this.$refs.form.reset()
                    this.submitLoading = false
                })
            }
        },

        deleteDelivery(deliveryStatusId) {
            this.submitLoading = true
            this.loading = true
            this.$store.dispatch('deliveryStatus/delete', {
                deliveryStatusId: deliveryStatusId
            }).then(() => {
                this.updateTable()
            })
            .finally(() => {
                this.deleteDialog = false
                this.submitLoading = false
            })
        },

        setInputsValues(deliveryStatusId) {
            this.name = this.deliveryStatus.find(c => c.id === deliveryStatusId)?.name
            this.invoiceType = this.deliveryStatus.find(c => c.id === deliveryStatusId)?.invoice_type
            this.isFinalStatus = this.deliveryStatus.find(c => c.id === deliveryStatusId)?.is_final_status
        },

        updateTable() {
            this.$store.dispatch('deliveryStatus/fetchAll').then(() => {
                this.loading = false
            })
        }
    },

    computed: {
        ...mapState({
            deliveryStatus: state => state.deliveryStatus.deliveryStatus
        }),

        headers() {
            return [
                {
                    text: this.$t('DeliveryStatus.headers.name'),
                    value: 'name',
                    name: 'name',
                    sortable: false
                }, 
                {
                    text: this.$t('DeliveryStatus.headers.invoice-type'),
                    value: 'invoice_type',
                    setValueExpr: value => this.invoicesTypes.find(c => c.id === value).name,
                    name: 'invoiceType',
                    sortable: false
                }, 
                {
                    text: this.$t('DeliveryStatus.headers.is-final-status'),
                    value: 'is_final_status',
                    name: 'isFinalStatus',
                    sortable: false
                },
                {
                    text: '',
                    value: 'actions',
                    name: 'actions',
                    align: 'end',
                    sortable: false
                }
            ]
        },
    },

    created() {
        this.updateTable()
    }
}
</script>